import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
    Plus, 
    CalendarBlank,
    PencilSimple,
    WhatsappLogo,
    Clock,
    Car,
    CaretLeft,
    CaretRight,
    Trash,
} from '@phosphor-icons/react';
import { format, addDays, startOfWeek, addMonths, subMonths, startOfMonth, endOfMonth, isSameDay, isSameMonth } from 'date-fns';
import { nl } from 'date-fns/locale';
import DayTimeline from './DayTimeline';
import { getPackageTheme } from '../utils/packageTheme';
import { AgendaSkeleton } from './Skeletons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonScheduler({ students, onLessonAdded }) {
    const [scheduledLessons, setScheduledLessons] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showAddModal, setShowAddModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('dag'); // 'dag' | 'week' | 'maand'
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [tick, setTick] = useState(0);

    // Live-tick so the "in les" pill updates minute-by-minute.
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(id);
    }, []);

    // Form state
    const [selectedStudent, setSelectedStudent] = useState('');
    const [lessonDate, setLessonDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [lessonTime, setLessonTime] = useState('09:00');
    const [duration, setDuration] = useState('60');

    // Compute end time from a "HH:MM" start + duration in minutes -> "HH:MM"
    const computeEndTime = (start, mins) => {
        if (!start || !mins) return '';
        const [h, m] = start.split(':').map(Number);
        const total = h * 60 + m + Number(mins);
        const eh = Math.floor((total / 60) % 24);
        const em = total % 60;
        return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
    };
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [whatsappData, setWhatsappData] = useState(null);

    // Edit modal state
    const [editLesson, setEditLesson] = useState(null);  // the lesson being edited
    const [editDate, setEditDate] = useState('');
    const [editTime, setEditTime] = useState('');
    const [editDuration, setEditDuration] = useState('60');
    const [editNotes, setEditNotes] = useState('');
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState('');

    const openEditModal = (lesson) => {
        setEditLesson(lesson);
        setEditDate(lesson.date);
        setEditTime(lesson.time || '09:00');
        setEditDuration(String(lesson.duration_minutes || 60));
        setEditNotes(lesson.notes || '');
        setEditError('');
    };

    const handleEditSave = async () => {
        if (!editLesson) return;
        setEditError('');
        setEditSubmitting(true);
        try {
            await axios.put(
                `${API}/scheduled-lessons/${editLesson.id}`,
                {
                    date: editDate,
                    time: editTime,
                    duration_minutes: parseInt(editDuration),
                    notes: editNotes,
                },
                { withCredentials: true }
            );
            // Surface WhatsApp resend modal
            setWhatsappData({
                name: editLesson.student_name,
                phone: editLesson.student_phone,
                date: editDate,
                time: editTime,
                duration: editDuration,
                isUpdate: true,
            });
            setEditLesson(null);
            await fetchScheduledLessons();
        } catch (e) {
            setEditError(e.response?.data?.detail || 'Wijzigen mislukt');
        } finally {
            setEditSubmitting(false);
        }
    };

    const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '').replace('api/', '') || 'https://leskaart-autopilot.nl';
    const LOGIN_URL = APP_URL.includes('preview.emergentagent.com') ? APP_URL : 'https://leskaart-autopilot.nl';

    const buildWhatsappUrl = (phone, studentName, date, time, duration, isUpdate = false) => {
        const endTime = computeEndTime(time, duration);
        const header = isUpdate
            ? `Hoi ${studentName}! 🚗\n\nJe rijles bij Rijschool AutoPilot is *gewijzigd*. De nieuwe afspraak:`
            : `Hoi ${studentName}! 🚗\n\nJe rijles bij Rijschool AutoPilot is ingepland:`;
        const message = `${header}\n\n📅 Datum: ${date}\n🕐 Tijd: ${time} - ${endTime}\n⏱ Duur: ${duration} minuten\n${notes ? `📝 ${notes}\n` : ''}\nBekijk je voortgang en leskaart:\n${LOGIN_URL}\n\n⚠️ Verzuim of verhindering?\nAls je niet tenminste 24 uur van tevoren afzegt, wordt de les volgens je lespakket in rekening gebracht (telt als een gereden les).\n\nTot dan! 👋\nRijschool AutoPilot - Drive like a pilot`;
        
        // Clean phone number
        let cleanPhone = (phone || '').replace(/[^0-9+]/g, '');
        if (cleanPhone.startsWith('06')) cleanPhone = '31' + cleanPhone.substring(1);
        if (cleanPhone.startsWith('+')) cleanPhone = cleanPhone.substring(1);
        
        return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    };

    useEffect(() => {
        fetchScheduledLessons();
    }, []);

    const fetchScheduledLessons = async () => {
        try {
            const res = await axios.get(`${API}/scheduled-lessons`, { withCredentials: true });
            setScheduledLessons(res.data);
        } catch (e) {
            console.error('Error fetching scheduled lessons:', e);
            // If endpoint doesn't exist yet, just set empty array
            setScheduledLessons([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!selectedStudent) {
            setError('Selecteer een leerling');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API}/scheduled-lessons`, {
                student_id: selectedStudent,
                date: lessonDate,
                time: lessonTime,
                duration_minutes: parseInt(duration),
                notes
            }, { withCredentials: true });
            
            // Show WhatsApp option
            const student = students.find(s => s.id === selectedStudent);
            if (student) {
                setWhatsappData({
                    name: student.name,
                    phone: student.phone,
                    date: lessonDate,
                    time: lessonTime,
                    duration
                });
            }
            
            setShowAddModal(false);
            setSelectedStudent('');
            setNotes('');
            fetchScheduledLessons();
            if (onLessonAdded) onLessonAdded();
        } catch (err) {
            setError(err.response?.data?.detail || 'Er is iets misgegaan');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteLesson = async (lessonId) => {
        if (!window.confirm('Weet je zeker dat je deze les wilt annuleren?')) return;
        
        try {
            await axios.delete(`${API}/scheduled-lessons/${lessonId}`, { withCredentials: true });
            fetchScheduledLessons();
        } catch (e) {
            console.error('Error deleting scheduled lesson:', e);
        }
    };

    const getStudent = (studentId) => {
        return students.find(s => s.id === studentId);
    };

    // Show skeleton while first fetch is in flight
    if (loading) {
        return <AgendaSkeleton />;
    }

    // Get lessons for selected date
    const lessonsForDate = scheduledLessons.filter(l => l.date === format(selectedDate, 'yyyy-MM-dd'));

    // Week view
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    // Week aggregates
    const weekLessons = scheduledLessons.filter(l => {
        const d = new Date(l.date);
        return d >= weekStart && d < addDays(weekStart, 7);
    });
    const weekTotalHours = weekLessons.reduce((sum, l) => sum + (l.duration_minutes || 60), 0) / 60;

    const isTodaySelected = isSameDay(selectedDate, new Date());
    const now = new Date();

    // Pending lessons (awaiting student confirmation)
    const pendingLessons = scheduledLessons.filter(l => l.confirmation_status === 'pending');

    // Find live "in progress" lesson right now
    const activeLesson = (() => {
        for (const l of scheduledLessons) {
            if (!l.time || !l.date) continue;
            const start = new Date(`${l.date}T${l.time}:00`);
            if (Number.isNaN(start.getTime())) continue;
            const end = new Date(start.getTime() + (l.duration_minutes || 60) * 60000);
            if (start <= now && now < end) return { lesson: l, start, end };
        }
        return null;
    })();

    const openWhatsappForLesson = (lesson) => {
        const student = getStudent(lesson.student_id);
        if (student) {
            const url = buildWhatsappUrl(student.phone, student.name, lesson.date, lesson.time, lesson.duration_minutes);
            window.open(url, '_blank');
        }
    };

    const openNewLesson = () => {
        setLessonDate(format(selectedDate, 'yyyy-MM-dd'));
        setShowAddModal(true);
    };

    // Compute "score" chip like the mockup — uses student's overall progress % (or lesson-specific score if available)
    const getLessonScore = (lesson) => {
        const student = getStudent(lesson.student_id);
        if (!student || !student.skill_scores) return null;
        const scores = Object.values(student.skill_scores).filter(v => typeof v === 'number' && v > 0);
        if (!scores.length) return null;
        return ((scores.reduce((a, b) => a + b, 0) / scores.length) / 5) * 100;
    };

    const isLessonPast = (lesson) => {
        if (!lesson.time || !lesson.date) return false;
        const start = new Date(`${lesson.date}T${lesson.time}:00`);
        if (Number.isNaN(start.getTime())) return false;
        const end = new Date(start.getTime() + (lesson.duration_minutes || 60) * 60000);
        return end <= now;
    };

    const getLessonStatus = (lesson) => {
        if (isLessonPast(lesson)) return { key: 'completed', label: 'voltooid', className: 'bg-[#1A202C] text-gray-300 border-[#4A5568]' };
        if (lesson.confirmation_status === 'pending') return { key: 'pending', label: 'in afwachting', className: 'bg-amber-500/15 text-amber-300 border-amber-500/40' };
        return { key: 'confirmed', label: 'ingepland', className: 'bg-[#D4A537]/15 text-[#E5B84A] border-[#D4A537]/40' };
    };

    // Section header ("VANDAAG, WOENSDAG, SEP 16" style)
    const sectionLabel = (() => {
        const prefix = isTodaySelected ? 'Vandaag, ' : '';
        return `${prefix}${format(selectedDate, 'EEEE, d MMM', { locale: nl })}`.toUpperCase();
    })();

    // Month grid days (used only in "Maand" view)
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const monthGridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const monthGridDays = Array.from({ length: 42 }, (_, i) => addDays(monthGridStart, i));
    // Header navigation: in Dag/Week view move by 7 days (one week);
    // in Maand view move by a full month so the grid re-renders per maand.
    const goPrev = () => setSelectedDate(viewMode === 'maand' ? subMonths(selectedDate, 1) : addDays(selectedDate, -7));
    const goNext = () => setSelectedDate(viewMode === 'maand' ? addMonths(selectedDate, 1) : addDays(selectedDate, 7));

    // Render a single lesson card in the "mobile" style from the mockup
    const renderLessonCard = (lesson) => {
        const student = getStudent(lesson.student_id);
        const theme = student ? getPackageTheme(student.package_type) : { primary: '#D4A537' };
        const endTime = computeEndTime(lesson.time, lesson.duration_minutes);
        const score = getLessonScore(lesson);
        const status = getLessonStatus(lesson);

        return (
            <div
                key={lesson.id}
                className="relative bg-[#2D3748] rounded-2xl overflow-hidden group hover:bg-[#354154] transition-colors"
                data-testid={`lesson-card-${lesson.id}`}
                onClick={() => openEditModal({ ...lesson, student_phone: student?.phone, student_name: student?.name })}
                role="button"
                tabIndex={0}
            >
                {/* Left accent bar */}
                <span
                    aria-hidden
                    className="absolute inset-y-3 left-0 w-1 rounded-r-full"
                    style={{ backgroundColor: theme.primary }}
                />
                <div className="p-4 pl-5">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 text-gray-300">
                            <Clock size={16} className="text-gray-400 flex-shrink-0" />
                            <span className="text-sm font-medium tabular-nums">{lesson.time} - {endTime}</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {score !== null && (
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold tabular-nums"
                                    style={{ backgroundColor: `${theme.primary}26`, color: theme.primary }}
                                    data-testid={`lesson-score-${lesson.id}`}
                                >
                                    <span className="w-3 h-3 inline-block rounded-sm" style={{ backgroundColor: theme.primary, opacity: 0.85 }} />
                                    {score.toFixed(1)}
                                </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${status.className}`}>
                                {status.label}
                            </span>
                        </div>
                    </div>
                    <div className="mt-2 text-white font-bold text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {student?.name || 'Onbekende leerling'}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-gray-400 text-sm">
                        <Car size={14} weight="fill" style={{ color: theme.primary }} />
                        <span>Rijles</span>
                        {lesson.notes && (
                            <>
                                <span className="mx-1 text-gray-600">•</span>
                                <span className="truncate">{lesson.notes}</span>
                            </>
                        )}
                    </div>

                    {/* Hover actions */}
                    <div className="mt-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openWhatsappForLesson(lesson); }}
                            className="flex-1 py-1.5 text-xs rounded-lg bg-green-500/15 hover:bg-green-500/25 text-green-300 border border-green-500/40 font-medium inline-flex items-center justify-center gap-1"
                            data-testid={`card-whatsapp-${lesson.id}`}
                        >
                            <WhatsappLogo size={14} weight="fill" /> WhatsApp
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openEditModal({ ...lesson, student_phone: student?.phone, student_name: student?.name }); }}
                            className="flex-1 py-1.5 text-xs rounded-lg bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 border border-blue-500/40 font-medium inline-flex items-center justify-center gap-1"
                            data-testid={`card-edit-${lesson.id}`}
                        >
                            <PencilSimple size={14} /> Wijzig
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDeleteLesson(lesson.id); }}
                            className="py-1.5 px-3 text-xs rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/40"
                            data-testid={`card-delete-${lesson.id}`}
                            aria-label="Verwijder"
                        >
                            <Trash size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4 pb-24" data-testid="lesson-scheduler-root">
            {/* View mode pill tabs */}
            <div className="grid grid-cols-3 gap-2 bg-[#1A202C] rounded-2xl p-1.5 border border-[#4A5568]">
                {[
                    { key: 'dag', label: 'Dag' },
                    { key: 'week', label: 'Week' },
                    { key: 'maand', label: 'Maand' },
                ].map(v => (
                    <button
                        key={v.key}
                        type="button"
                        onClick={() => setViewMode(v.key)}
                        className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${
                            viewMode === v.key
                                ? 'bg-[#D4A537] text-[#1A202C] shadow-lg shadow-[#D4A537]/20'
                                : 'text-gray-300 hover:text-white hover:bg-[#2D3748]'
                        }`}
                        data-testid={`view-mode-${v.key}`}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {/* Month/period header */}
            <div className="bg-[#1A202C] rounded-2xl border border-[#4A5568] p-4">
                <div className="flex items-center justify-between mb-3">
                    <button
                        type="button"
                        onClick={goPrev}
                        className="w-9 h-9 rounded-full text-[#D4A537] hover:bg-[#D4A537]/10 flex items-center justify-center transition-colors"
                        data-testid="period-prev"
                        aria-label={viewMode === 'maand' ? 'Vorige maand' : 'Vorige week'}
                    >
                        <CaretLeft size={18} weight="bold" />
                    </button>
                    <h3 className="text-lg font-semibold text-white capitalize" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {format(selectedDate, 'MMMM yyyy', { locale: nl })}
                    </h3>
                    <button
                        type="button"
                        onClick={goNext}
                        className="w-9 h-9 rounded-full text-[#D4A537] hover:bg-[#D4A537]/10 flex items-center justify-center transition-colors"
                        data-testid="period-next"
                        aria-label={viewMode === 'maand' ? 'Volgende maand' : 'Volgende week'}
                    >
                        <CaretRight size={18} weight="bold" />
                    </button>
                </div>

                {viewMode !== 'maand' && (
                    <>
                        {/* Weekday letters */}
                        <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-widest text-gray-500 font-medium mb-1.5">
                            {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(w => (
                                <div key={w}>{w}</div>
                            ))}
                        </div>
                        {/* Week strip */}
                        <div className="grid grid-cols-7 gap-1">
                            {weekDays.map(day => {
                                const dayKey = format(day, 'yyyy-MM-dd');
                                const dayLessons = scheduledLessons.filter(l => l.date === dayKey);
                                const isToday = isSameDay(day, new Date());
                                const isSelected = isSameDay(day, selectedDate);
                                return (
                                    <button
                                        type="button"
                                        key={day.toISOString()}
                                        onClick={() => setSelectedDate(day)}
                                        className="relative flex flex-col items-center py-1.5 group"
                                        data-testid={`week-day-${dayKey}`}
                                    >
                                        <span
                                            className={`w-11 h-11 rounded-full flex items-center justify-center text-lg font-semibold tabular-nums transition-all ${
                                                isSelected
                                                    ? 'bg-[#D4A537] text-[#1A202C] shadow-lg shadow-[#D4A537]/30 scale-105'
                                                    : isToday
                                                        ? 'bg-transparent border-2 border-[#D4A537] text-white'
                                                        : 'bg-transparent text-gray-200 group-hover:bg-[#2D3748]'
                                            }`}
                                            style={{ fontFamily: 'Outfit, sans-serif' }}
                                        >
                                            {format(day, 'd')}
                                        </span>
                                        {dayLessons.length > 0 && (
                                            <div className="mt-1 flex items-center gap-0.5">
                                                {dayLessons.slice(0, 3).map(l => {
                                                    const student = getStudent(l.student_id);
                                                    const theme = student ? getPackageTheme(student.package_type) : { primary: '#D4A537' };
                                                    return (
                                                        <span
                                                            key={l.id}
                                                            className="w-1 h-1 rounded-full"
                                                            style={{ backgroundColor: isSelected ? '#1A202C' : theme.primary }}
                                                        />
                                                    );
                                                })}
                                                {dayLessons.length > 3 && (
                                                    <span className={`text-[8px] font-bold ${isSelected ? 'text-[#1A202C]' : 'text-gray-400'}`}>+</span>
                                                )}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {/* handle indicator */}
                        <div className="mt-3 mx-auto w-14 h-1 rounded-full bg-[#4A5568]/70" aria-hidden />
                    </>
                )}

                {viewMode === 'maand' && (
                    <div>
                        <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-widest text-gray-500 font-medium mb-1.5">
                            {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(w => (
                                <div key={w}>{w}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                            {monthGridDays.map(day => {
                                const dayKey = format(day, 'yyyy-MM-dd');
                                const dayLessons = scheduledLessons.filter(l => l.date === dayKey);
                                const isToday = isSameDay(day, new Date());
                                const isSelected = isSameDay(day, selectedDate);
                                const outside = !isSameMonth(day, selectedDate);
                                return (
                                    <button
                                        type="button"
                                        key={day.toISOString()}
                                        onClick={() => setSelectedDate(day)}
                                        className={`relative aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-all ${
                                            isSelected
                                                ? 'bg-[#D4A537] text-[#1A202C] font-bold shadow-md'
                                                : isToday
                                                    ? 'border-2 border-[#D4A537] text-white font-semibold'
                                                    : outside
                                                        ? 'text-gray-600 hover:bg-[#2D3748]'
                                                        : 'text-gray-200 hover:bg-[#2D3748]'
                                        }`}
                                        data-testid={`month-day-${dayKey}`}
                                    >
                                        <span className="tabular-nums">{format(day, 'd')}</span>
                                        {dayLessons.length > 0 && (
                                            <span
                                                className={`absolute bottom-1 w-1 h-1 rounded-full ${isSelected ? 'bg-[#1A202C]' : 'bg-[#D4A537]'}`}
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Primary + secondary CTA */}
            <div className="space-y-2">
                <Button
                    onClick={openNewLesson}
                    className="w-full h-12 bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold text-base rounded-2xl shadow-md shadow-[#D4A537]/20"
                    data-testid="schedule-lesson-button"
                >
                    <Plus size={20} weight="bold" className="mr-2" />
                    Nieuwe Les
                </Button>
                <button
                    type="button"
                    onClick={() => setShowPendingModal(true)}
                    className="relative w-full h-12 rounded-2xl bg-transparent border border-[#D4A537]/40 text-[#E5B84A] hover:bg-[#D4A537]/10 font-medium inline-flex items-center justify-center gap-2 transition-colors"
                    data-testid="show-pending-button"
                >
                    <Clock size={18} />
                    Toon alle rijlessen in afwachting
                    {pendingLessons.length > 0 && (
                        <span
                            className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shadow-lg shadow-red-500/30 animate-pulse"
                            data-testid="pending-count-badge"
                        >
                            {pendingLessons.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Body — differs per view mode */}
            {viewMode === 'dag' && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between px-1">
                        <h4 className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                            {sectionLabel}
                        </h4>
                        {!isTodaySelected && (
                            <button
                                type="button"
                                onClick={() => setSelectedDate(new Date())}
                                className="text-xs text-[#D4A537] hover:text-[#E5B84A] font-medium"
                                data-testid="schedule-today-button"
                            >
                                Ga naar vandaag
                            </button>
                        )}
                    </div>

                    {lessonsForDate.length === 0 ? (
                        <div className="bg-[#1A202C] rounded-2xl border border-dashed border-[#4A5568] p-10 text-center">
                            <CalendarBlank size={40} className="mx-auto mb-2 text-gray-500 opacity-70" />
                            <p className="text-gray-400 text-sm mb-4">Geen lessen gepland voor deze dag</p>
                            <Button
                                variant="outline"
                                className="border-[#D4A537]/40 text-[#E5B84A] hover:bg-[#D4A537]/10 hover:text-[#D4A537] rounded-xl"
                                onClick={openNewLesson}
                                data-testid="empty-day-add-lesson"
                            >
                                <Plus size={16} className="mr-2" />
                                Les toevoegen
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {lessonsForDate
                                .slice()
                                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                                .map(renderLessonCard)}
                        </div>
                    )}
                </div>
            )}

            {viewMode === 'week' && (
                <div className="bg-[#1A202C] rounded-2xl border border-[#4A5568] p-4 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <h4 className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                            Week — {format(weekStart, 'd MMM', { locale: nl })} – {format(addDays(weekStart, 6), 'd MMM', { locale: nl })}
                        </h4>
                        <div className="flex items-center gap-2 text-[11px]">
                            <span className="px-2 py-0.5 rounded-md bg-[#2D3748] border border-[#4A5568] text-gray-300 tabular-nums">
                                {weekLessons.length} lessen
                            </span>
                            <span className="px-2 py-0.5 rounded-md bg-[#D4A537]/10 border border-[#D4A537]/40 text-[#E5B84A] font-semibold tabular-nums">
                                {weekTotalHours.toFixed(1)} uur
                            </span>
                        </div>
                    </div>
                    {lessonsForDate.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 text-sm">Geen lessen voor {format(selectedDate, 'EEEE d MMM', { locale: nl })}</div>
                    ) : (
                        <DayTimeline
                            lessons={lessonsForDate}
                            getStudent={getStudent}
                            isToday={isTodaySelected}
                            onEdit={(lesson) => openEditModal({ ...lesson, student_phone: getStudent(lesson.student_id)?.phone, student_name: getStudent(lesson.student_id)?.name })}
                            onDelete={handleDeleteLesson}
                            onWhatsapp={openWhatsappForLesson}
                        />
                    )}
                </div>
            )}

            {viewMode === 'maand' && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between px-1">
                        <h4 className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                            {sectionLabel}
                        </h4>
                    </div>
                    {lessonsForDate.length === 0 ? (
                        <div className="bg-[#1A202C] rounded-2xl border border-dashed border-[#4A5568] p-10 text-center">
                            <p className="text-gray-400 text-sm mb-4">Geen lessen op deze dag</p>
                            <Button
                                variant="outline"
                                className="border-[#D4A537]/40 text-[#E5B84A] hover:bg-[#D4A537]/10 hover:text-[#D4A537] rounded-xl"
                                onClick={openNewLesson}
                            >
                                <Plus size={16} className="mr-2" />
                                Les toevoegen
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {lessonsForDate
                                .slice()
                                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                                .map(renderLessonCard)}
                        </div>
                    )}
                </div>
            )}

            {/* Live "In les met ..." floating pill */}
            {activeLesson && (() => {
                const student = getStudent(activeLesson.lesson.student_id);
                const theme = student ? getPackageTheme(student.package_type) : { primary: '#D4A537' };
                const elapsedMin = Math.max(0, Math.floor((now - activeLesson.start) / 60000));
                const remainingMin = Math.max(0, Math.round((activeLesson.end - now) / 60000));
                return (
                    <div className="fixed left-3 right-3 bottom-20 z-40 pointer-events-none flex justify-center" data-testid="active-lesson-pill">
                        <button
                            type="button"
                            onClick={() => openEditModal({ ...activeLesson.lesson, student_phone: student?.phone, student_name: student?.name })}
                            className="pointer-events-auto max-w-md w-full rounded-full border shadow-xl backdrop-blur-md flex items-center gap-3 px-3 py-2 transition-transform hover:scale-[1.02]"
                            style={{
                                background: `linear-gradient(90deg, ${theme.primary}F0, ${theme.primary}D8)`,
                                borderColor: `${theme.primary}80`,
                                boxShadow: `0 12px 40px -8px ${theme.primary}80`,
                            }}
                        >
                            <span className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                                <Car size={20} weight="fill" className="text-[#1A202C]" />
                            </span>
                            <div className="flex-1 min-w-0 text-left text-[#1A202C]">
                                <div className="font-bold text-sm truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    In les met {student?.name || '...'}
                                </div>
                                <div className="text-[11px] font-medium opacity-90 tabular-nums">
                                    {elapsedMin} min verstreken · {remainingMin} min te gaan
                                </div>
                            </div>
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-[0_0_10px_2px_rgba(52,211,153,0.9)] animate-pulse flex-shrink-0" />
                        </button>
                    </div>
                );
            })()}

            {/* Pending lessons drawer */}
            <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
                <DialogContent className="sm:max-w-md bg-[#2D3748] border-[#4A5568] text-white max-h-[80vh] overflow-y-auto rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Clock size={22} className="text-[#D4A537]" weight="fill" />
                            Rijlessen in afwachting
                        </DialogTitle>
                    </DialogHeader>
                    {pendingLessons.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">
                            Geen lessen wachten op bevestiging 🎉
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {pendingLessons
                                .sort((a, b) => `${a.date}T${a.time || ''}`.localeCompare(`${b.date}T${b.time || ''}`))
                                .map(lesson => {
                                    const student = getStudent(lesson.student_id);
                                    return (
                                        <div
                                            key={lesson.id}
                                            className="p-3 rounded-xl bg-[#1A202C] border border-[#4A5568] flex items-center justify-between gap-3"
                                            data-testid={`pending-item-${lesson.id}`}
                                        >
                                            <div className="min-w-0">
                                                <div className="font-semibold text-white truncate">{student?.name || 'Onbekend'}</div>
                                                <div className="text-xs text-gray-400 tabular-nums">
                                                    {lesson.date} · {lesson.time} – {computeEndTime(lesson.time, lesson.duration_minutes)}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => { setShowPendingModal(false); setSelectedDate(new Date(lesson.date)); }}
                                                className="text-xs px-3 py-1.5 rounded-lg bg-[#D4A537]/15 text-[#E5B84A] border border-[#D4A537]/40 hover:bg-[#D4A537]/25"
                                            >
                                                Open
                                            </button>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Add Lesson Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="sm:max-w-md bg-[#2D3748] border-[#4A5568] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Les Inplannen
                        </DialogTitle>
                    </DialogHeader>

                    {error && (
                        <div className="p-3 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-gray-300">Leerling *</Label>
                            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                                <SelectTrigger className="bg-[#1A202C] border-[#4A5568] text-white" data-testid="schedule-student-select">
                                    <SelectValue placeholder="Selecteer een leerling" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#2D3748] border-[#4A5568]">
                                    {[...students]
                                        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl', { sensitivity: 'base' }))
                                        .map(student => (
                                        <SelectItem 
                                            key={student.id} 
                                            value={student.id}
                                            className="text-white hover:bg-[#3D4A5C]"
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                {student.name}
                                                {(student.external || student.package_type === 'nationale_rijschool') && (
                                                    <span className="text-[9px] uppercase tracking-widest font-bold rounded px-1.5 py-[1px] bg-[#F97316]/20 text-[#FB923C] border border-[#F97316]/50">
                                                        Extern
                                                    </span>
                                                )}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-gray-300">Datum</Label>
                                <Input
                                    type="date"
                                    value={lessonDate}
                                    onChange={(e) => setLessonDate(e.target.value)}
                                    className="bg-[#1A202C] border-[#4A5568] text-white"
                                    data-testid="schedule-date-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-300">Tijd</Label>
                                <Select value={lessonTime} onValueChange={setLessonTime}>
                                    <SelectTrigger className="bg-[#1A202C] border-[#4A5568] text-white" data-testid="schedule-time-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#2D3748] border-[#4A5568] max-h-60">
                                        {Array.from({ length: 23 }, (_, i) => {
                                            const h = 8 + Math.floor(i / 2);
                                            const m = i % 2 === 0 ? '00' : '30';
                                            if (h > 19) return null;
                                            if (h === 19 && m === '30') return null;
                                            const t = `${h.toString().padStart(2, '0')}:${m}`;
                                            return <SelectItem key={t} value={t} className="text-white hover:bg-[#3D4A5C]">{t}</SelectItem>;
                                        }).filter(Boolean)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-300">Duur</Label>
                            <Select value={duration} onValueChange={setDuration}>
                                <SelectTrigger className="bg-[#1A202C] border-[#4A5568] text-white" data-testid="schedule-duration-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#2D3748] border-[#4A5568]">
                                    <SelectItem value="45" className="text-white hover:bg-[#3D4A5C]">45 minuten</SelectItem>
                                    <SelectItem value="60" className="text-white hover:bg-[#3D4A5C]">60 minuten</SelectItem>
                                    <SelectItem value="90" className="text-white hover:bg-[#3D4A5C]">90 minuten</SelectItem>
                                    <SelectItem value="120" className="text-white hover:bg-[#3D4A5C]">120 minuten</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-300">Notities</Label>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Bijv. oefenen op rotondes, examentraining..."
                                rows={3}
                                className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500"
                                data-testid="schedule-notes-input"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowAddModal(false)}
                                className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                            >
                                Annuleren
                            </Button>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                                data-testid="schedule-submit-button"
                            >
                                {submitting ? 'Bezig...' : 'Inplannen'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Lesson Modal */}
            <Dialog open={!!editLesson} onOpenChange={(open) => { if (!open) setEditLesson(null); }}>
                <DialogContent className="sm:max-w-md bg-[#2D3748] border-[#4A5568] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <PencilSimple size={22} weight="fill" className="text-blue-400" />
                            Les wijzigen
                        </DialogTitle>
                    </DialogHeader>
                    {editLesson && (
                        <div className="space-y-4 mt-2">
                            <div className="text-sm text-gray-400">
                                Leerling: <span className="text-white font-medium">{editLesson.student_name}</span>
                            </div>
                            {editError && (
                                <div className="bg-red-900/30 text-red-300 text-sm p-2 rounded">{editError}</div>
                            )}
                            <div>
                                <Label className="text-gray-300 text-sm">Datum</Label>
                                <Input
                                    type="date"
                                    value={editDate}
                                    onChange={(e) => setEditDate(e.target.value)}
                                    className="bg-[#1A202C] border-[#4A5568] text-white mt-1"
                                    data-testid="edit-lesson-date"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-gray-300 text-sm">Tijd</Label>
                                    <Input
                                        type="time"
                                        value={editTime}
                                        onChange={(e) => setEditTime(e.target.value)}
                                        className="bg-[#1A202C] border-[#4A5568] text-white mt-1"
                                        data-testid="edit-lesson-time"
                                    />
                                </div>
                                <div>
                                    <Label className="text-gray-300 text-sm">Duur (min)</Label>
                                    <Select value={editDuration} onValueChange={setEditDuration}>
                                        <SelectTrigger className="bg-[#1A202C] border-[#4A5568] text-white mt-1" data-testid="edit-lesson-duration">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#2D3748] border-[#4A5568] text-white">
                                            {['30', '45', '60', '75', '90', '105', '120'].map(d => (
                                                <SelectItem key={d} value={d}>{d} min</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500">
                                Eindtijd: <span className="text-[#D4A537] font-medium">{computeEndTime(editTime, editDuration)}</span>
                            </div>
                            <div>
                                <Label className="text-gray-300 text-sm">Notitie</Label>
                                <Textarea
                                    value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    className="bg-[#1A202C] border-[#4A5568] text-white mt-1"
                                    rows={2}
                                    data-testid="edit-lesson-notes"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setEditLesson(null)}
                                    className="flex-1 border-[#4A5568] text-gray-300 hover:bg-[#1A202C]"
                                >
                                    Annuleren
                                </Button>
                                <Button
                                    onClick={handleEditSave}
                                    disabled={editSubmitting}
                                    className="flex-1 bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold"
                                    data-testid="edit-lesson-save"
                                >
                                    {editSubmitting ? 'Bezig...' : 'Opslaan'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* WhatsApp popup after scheduling */}
            <Dialog open={!!whatsappData} onOpenChange={() => setWhatsappData(null)}>
                <DialogContent className="sm:max-w-md bg-[#2D3748] border-[#4A5568] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <WhatsappLogo size={28} weight="fill" className="text-green-400" />
                            {whatsappData?.isUpdate ? 'Les Gewijzigd!' : 'Les Ingepland!'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {whatsappData && (
                        <div className="space-y-4">
                            <div className="bg-[#1A202C] rounded-lg p-4 border border-[#4A5568]">
                                <div className="text-sm text-gray-400 mb-3">Lesdetails:</div>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Leerling</span>
                                        <span className="text-white font-medium">{whatsappData.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Datum</span>
                                        <span className="text-white font-medium">{whatsappData.date}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Tijd</span>
                                        <span className="text-white font-medium">{whatsappData.time} – {computeEndTime(whatsappData.time, whatsappData.duration)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Duur</span>
                                        <span className="text-white font-medium">{whatsappData.duration} min</span>
                                    </div>
                                </div>
                            </div>

                            <p className="text-sm text-gray-400">
                                Stuur de lesdetails + inloglink naar {whatsappData.name} via WhatsApp:
                            </p>

                            <div className="flex gap-3">
                                <Button
                                    onClick={() => {
                                        const url = buildWhatsappUrl(whatsappData.phone, whatsappData.name, whatsappData.date, whatsappData.time, whatsappData.duration, !!whatsappData.isUpdate);
                                        window.open(url, '_blank');
                                        setWhatsappData(null);
                                        fetchScheduledLessons();
                                    }}
                                    className="flex-1 bg-[#25D366] text-white hover:bg-[#1EBE57] font-semibold text-base py-5"
                                    data-testid="whatsapp-send-button"
                                >
                                    <WhatsappLogo size={22} weight="fill" className="mr-2" />
                                    Stuur via WhatsApp
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => { setWhatsappData(null); fetchScheduledLessons(); }}
                                    className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                                >
                                    Sluiten
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
